import styled, { keyframes } from 'styled-components';
import { useState, useEffect } from 'react';
import { LoadingBadge } from './LoadingBadge';
import { FireLevel } from './FireLevel/FireLevel';
import { StarBand } from './StarBand/StarBand';
import { Bronze } from './Frame/Bronze';
import { Silver } from './Frame/Silver';
import { Gold } from './Frame/Gold';
import { Diamond } from './Frame/Diamond';

const fadeInScale = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

export const Badge = ({
  isLoading,
  level = 1,
}) => {
  const [showActualBadge, setShowActualBadge] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!isLoading && !showActualBadge) {
      // Start exit animation for loading badge
      setIsExiting(true);
      // Show actual badge after loading badge exit animation (200ms)
      const timer = setTimeout(() => {
        setShowActualBadge(true);
      }, 200);
      return () => clearTimeout(timer);
    } else if (isLoading) {
      // Reset states when going back to loading
      setShowActualBadge(false);
      setIsExiting(false);
    }
  }, [isLoading, showActualBadge]);

  if (isLoading || !showActualBadge) {
    return <LoadingBadge isExiting={isExiting} />;
  }

  const badge = () => {
    if (level === 1) {
      return <Bronze />
    }
    if (level === 2) {
      return <Silver />
    }
    if (level === 3) {
      return <Gold />
    }
    if (level === 4) {
      return <DiamondWrapper><Diamond /></DiamondWrapper>
    }

    return (
      <BadgeContainer>
        <GlowLayer2>
          <svg xmlns="http://www.w3.org/2000/svg" width="305" height="335" viewBox="0 0 305 335" fill="none">
            <path d="M280.93 66.256L176.564 6.3978C169.256 2.2053 160.936 0 152.506 0C144.076 0 135.744 2.21742 128.436 6.3978L24.0574 66.256C9.22283 74.7622 0 90.6234 0 107.648V227.352C0 244.377 9.22283 260.238 24.0574 268.744L128.436 328.602C135.744 332.795 144.064 335 152.506 335C160.948 335 169.268 332.783 176.564 328.602L280.943 268.744C295.789 260.238 305 244.377 305 227.352V107.648C305 90.6234 295.777 74.7622 280.943 66.256H280.93ZM287.652 227.364C287.652 238.282 281.784 248.375 272.257 253.84L167.878 313.698C158.35 319.163 146.614 319.163 137.098 313.698L32.7191 253.84C23.1913 248.375 17.3233 238.282 17.3233 227.364V107.66C17.3233 96.7425 23.1913 86.649 32.7191 81.1842L137.11 21.326C146.638 15.8612 158.374 15.8612 167.89 21.326L272.269 81.1842C281.797 86.649 287.665 96.7425 287.665 107.66V227.364H287.652Z" fill="#FFE55D" />
          </svg>
        </GlowLayer2>
        <GlowLayer1>
          <svg xmlns="http://www.w3.org/2000/svg" width="257" height="283" viewBox="0 0 257 283" fill="none">
            <path d="M236.718 55.9715L148.776 5.40471C142.619 1.86299 135.608 0 128.505 0C121.402 0 114.381 1.87322 108.224 5.40471L20.2713 55.9715C7.77137 63.1573 0 76.5565 0 90.9383V192.062C0 206.444 7.77137 219.843 20.2713 227.028L108.224 277.595C114.381 281.137 121.392 283 128.505 283C135.619 283 142.629 281.127 148.776 277.595L236.729 227.028C249.239 219.843 257 206.444 257 192.062V90.9383C257 76.5565 249.229 63.1573 236.729 55.9715H236.718ZM242.382 192.072C242.382 201.295 237.438 209.821 229.41 214.438L141.457 265.005C133.429 269.621 123.54 269.621 115.522 265.005L27.5699 214.438C19.5415 209.821 14.597 201.295 14.597 192.072V90.9486C14.597 81.7258 19.5415 73.199 27.5699 68.5825L115.532 18.0157C123.561 13.3992 133.45 13.3992 141.468 18.0157L229.42 68.5825C237.448 73.199 242.393 81.7258 242.393 90.9486V192.072H242.382Z" fill="#FFE55D" />
          </svg>
        </GlowLayer1>
        <LaurelLeft>
          <svg xmlns="http://www.w3.org/2000/svg" width="105" height="75" viewBox="0 0 105 75" fill="none">
            <path d="M59.8418 50.0517C59.8418 50.0517 48.7081 31.2626 25.1724 33.4299C20.6716 33.8341 42.612 55.4757 59.8418 50.0517Z" fill="#FFD440" />
            <path d="M91.1644 67.7768C91.1644 67.7768 78.3646 50.0745 55.1284 54.3718C50.6838 55.1834 74.5038 74.7449 91.1644 67.7768Z" fill="#FFD440" />
            <path d="M70.7437 50.2088C70.7437 50.2088 68.9996 28.4634 46.8826 20.0926C42.6575 18.4826 52.8639 47.5252 70.7437 50.2088Z" fill="#FFD440" />
            <path d="M43.7107 32.3399C43.7107 32.3399 39.6232 10.9056 16.7339 4.96713C12.3601 3.82184 25.6486 31.5995 43.7107 32.3399Z" fill="#FFD440" />
          </svg>
        </LaurelLeft>
        <LaurelRight>
          <svg xmlns="http://www.w3.org/2000/svg" width="105" height="75" viewBox="0 0 105 75" fill="none">
            <path d="M59.8418 50.0517C59.8418 50.0517 48.7081 31.2626 25.1724 33.4299C20.6716 33.8341 42.612 55.4757 59.8418 50.0517Z" fill="#FFD440" />
            <path d="M91.1644 67.7768C91.1644 67.7768 78.3646 50.0745 55.1284 54.3718C50.6838 55.1834 74.5038 74.7449 91.1644 67.7768Z" fill="#FFD440" />
            <path d="M70.7437 50.2088C70.7437 50.2088 68.9996 28.4634 46.8826 20.0926C42.6575 18.4826 52.8639 47.5252 70.7437 50.2088Z" fill="#FFD440" />
            <path d="M43.7107 32.3399C43.7107 32.3399 39.6232 10.9056 16.7339 4.96713C12.3601 3.82184 25.6486 31.5995 43.7107 32.3399Z" fill="#FFD440" />
          </svg>
        </LaurelRight>
        <BadgeFrameWrapper>
          {/* <BadgeFrame /> */}
        </BadgeFrameWrapper>
        <FireLevelWrapper>
          <FireLevel level={level} />
        </FireLevelWrapper>
        <StarBandWrapper>
          <StarBand level={level} />
        </StarBandWrapper>
      </BadgeContainer>
    )
  }

  return (
    <Container>
      <BadgeContainer>
        {badge()}
      </BadgeContainer>
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 275px;
  height: 296px;
  z-index: 2;
  animation: ${fadeInScale} 0.3s ease-out forwards;
  animation-delay: 0s;
  opacity: 0;
`;

const BadgeContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 365px;
    height: auto;
  }
`;

const DiamondWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 380px;
    height: auto;
  }
`;

const GlowLayer1 = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 257px;
  height: 283px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: -2;
  opacity: 0.6;
  filter: blur(8px);
`;

const GlowLayer2 = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 305px;
  height: 335px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: -3;
  opacity: 0.4;
  filter: blur(15px);
`;

const BadgeFrameWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FireLevelWrapper = styled.div`
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
`;

const StarBandWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
`;

const LaurelLeft = styled.div`
  position: absolute;
  left: -28px;
  bottom: 74px;
  z-index: 1;
`;

const LaurelRight = styled.div`
  position: absolute;
  right: -28px;
  bottom: 74px;
  z-index: 1;
  transform: scaleX(-1);
`;
